import { APIDataDocument, APIDataDocumentSchemaRevision, DocumentTileData } from '@evertel/types'
import { injectable, inject, decorate } from '@evertel/di'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
// evertel
import { Api } from '@evertel/api'
import { DocumentStore, DocumentSchemaStore, DocumentSchemaRevisionStore, DocumentTileStore, DocumentTileWithFetchDate } from '../store'
import moment from 'moment'

// how long a cached document or tile is used before it is refetched
const CACHE_MAX_AGE_MINUTES = 5

// what a tile request answers with when this user can't see the document (Loopback reports an
// ACL denial as 401 by default)
const NO_ACCESS_STATUSES = [401, 403, 404]

const isOlderThanCacheMaxAge = (fetchedDate?: moment.Moment): boolean =>
    !fetchedDate || fetchedDate.isBefore(moment().subtract(CACHE_MAX_AGE_MINUTES, 'minutes'))

const isTileStale = (tile: DocumentTileWithFetchDate, since?: moment.MomentInput): boolean => {
    if (isOlderThanCacheMaxAge(tile.fetchedDate)) return true

    // compares the client's clock with the server's, so skew can cost a spare refetch or miss
    // one; fine for a freshness hint, the age limit above still applies either way
    return !!since && tile.fetchedDate.isBefore(moment(since))
}

class DocumentViewController {
    documentId = 0
    _name = ''
    documentSchemaId = 0
    documentSchemaRevisionId = 0
    loaded = false
    deleted = false
    myAccess: any = {}
    canEdit = false
    canShare = false

    constructor(
        protected documentStore: DocumentStore,
        protected documentSchemaStore: DocumentSchemaStore,
        protected documentSchemaRevisionStore: DocumentSchemaRevisionStore,
        protected documentTileStore: DocumentTileStore,
        protected api: Api
    ) {
        //MUST be makeObservable, makeAutoObservable breaks extended classes (DocumentEditController extends this)
        makeObservable(this, {
            documentId: observable,
            _name: observable,
            name: computed,
            documentSchemaId: observable,
            documentSchemaRevisionId: observable,
            loaded: observable,
            deleted: observable,
            myAccess: observable,
            canEdit: observable,
            canShare: observable,
            tile: computed,
            load: action,
            loadFromStoreOrFetch: action,
            loadTile: action,
            setArchive: action,
            delete: action,
            document: computed,
            documentSchema: computed
        })
    }

    get name(): string {
        return this.document?.name?.trim()
    }

    async load(documentId: number, ...args: any[]) {
        if (!documentId) throw new Error('Missing a document ID!')

        this.loaded = false

        const document = await this.fetchDocument(documentId)

        if (!document) return

        this.processLoadedDocument(document)

        runInAction(() => {
            this.loaded = true
        })

        return this.document
    }

    protected processLoadedDocument(document: APIDataDocument) {
        this.documentStore.update(document)
        this.documentSchemaStore.update(document?.documentSchema)
        this.documentSchemaRevisionStore.update(document?.documentSchemaRevision)

        runInAction(() => {
            this.documentId = document?.id || 0
            this.documentSchemaId = document?.documentSchemaId || 0
            this.documentSchemaRevisionId = document?.documentSchemaRevisionId || 0
        })
    }

    loadFromStoreOrFetch = async (documentId: number) => {
        if (!documentId) throw new Error('Missing a document ID!')

        this.loaded = false
        const document = this.documentStore.findById(documentId)

        const isStale = isOlderThanCacheMaxAge(document?.fetchedDate)

        if (!document || isStale) {
            // console.log(':::::: DocumentViewController: Unable to find cached document, loading.', documentId)
            //no document or too old, so fetch again
            return await this.load(documentId)
        }
        // console.log(':::::: DocumentViewController: 🎈🎈🎈🎈🎈🎈', documentId)

        // if it was too old it intentionally still falls through to here
        // so we can show the older info while the new info is updated
        // in that case this.loaded might be ambiguous...

        runInAction(() => {
            this.documentId = documentId || 0
            this.documentSchemaId = document?.documentSchemaId || 0
            this.documentSchemaRevisionId = document?.documentSchemaRevisionId || 0
            this.loaded = true
        })

        return this.document

    }

    get tile(): DocumentTileWithFetchDate | undefined {
        return this.documentTileStore.findById(this.documentId)
    }

    /**
     * Loads the room tile for a document, reusing the cached one while it is still current.
     * @param since when the thing showing the tile happened (e.g. the share or update message
     * was posted). A cached tile fetched before then predates it and is refetched, so a new
     * update message never shows the values from before that update
     */
    loadTile = async (documentId: number, { since }: { since?: moment.MomentInput } = {}) => {
        if (!documentId) throw new Error('Missing a document ID!')

        runInAction(() => {
            this.documentId = documentId
        })

        const tile = this.documentTileStore.findById(documentId)

        // a stale tile keeps showing while the new one is on its way, so scrolling back to a
        // message doesn't flash the skeleton
        if (tile && !isTileStale(tile, since)) return

        await this.fetchTile(documentId)
    }

    /**
     * Refetches the tile, but only if one has been loaded: nothing is showing a document
     * nobody has fetched a tile for, so there is nothing to bring up to date
     */
    refreshTile = async (documentId = this.documentId) => {
        if (!this.documentTileStore.findById(documentId)) return

        await this.fetchTile(documentId)
    }

    protected fetchTile = async (documentId: number) => {
        // tiles for the same document that mount together share the one request
        const inflight = this.documentTileStore.getInflightFetch(documentId)
        if (inflight) {
            await inflight
            return
        }

        const promise = this.api.Routes.Document.getTile(documentId)
            .then(tile => this.documentTileStore.update(tile as DocumentTileData))
            .catch(error => {
                // the share was removed or the document is gone: remember it so the tile says
                // so instead of fetching again on every mount. Anything else (network, server)
                // is left to retry on the next mount. An expired session also lands here, but
                // it logs the user out moments later and the stores are cleared with it
                if (NO_ACCESS_STATUSES.includes(error?.status)) {
                    this.documentTileStore.setNoAccess(documentId)
                    return
                }

                throw error
            })
            .finally(() => {
                this.documentTileStore.clearInflightFetch(documentId)
            })

        this.documentTileStore.setInflightFetch(documentId, promise)

        await promise
    }

    protected getDefaultIncludes(): Array<string | {relation: string, scope?: {include: string | string[]}}> {
        return [
            'media',
            { relation: 'documentSchema', scope: { include: 'iconMedia' } },
            'documentSchemaRevision'
        ]
    }

    fetchDocument = async (documentId: number): Promise<APIDataDocument> => {
        return await this.api.Routes.Document.getById(documentId, {
            include: this.getDefaultIncludes()
        })
    }

    fetchDocumentRevisions = async (documentSchemaId: number, filter: any): Promise<APIDataDocumentSchemaRevision[]> => {
        return await this.api.Routes.DocumentSchema.getRevisions(documentSchemaId, filter)
    }

    setArchive = async (isArchived: boolean): Promise<APIDataDocument> => {
        const response = await this.api.Routes.Document.putById(this.documentId, { isArchived })

        runInAction(() => {
            this.documentStore.update(response)
        })

        // the tile shows the archived badge, so bring any tile on screen up to date. Not awaited:
        // archiving has succeeded either way, and a failed refresh retries on the next mount
        this.refreshTile(response.id as number).catch(() => undefined)

        return this.documentStore.findById(response.id as number) as APIDataDocument
    }

    delete = async () => {
        // can only delete documents not previously shared
        const resp = await this.api.Routes.Document.delById(this.documentId)

        runInAction(() => {
            // if deleted, remove from the DocumentStore
            if (resp?.count) {
                // Give UI enough time to navigate away from screen.
                // Without this timeout, mobx triggers the effects that attempt to
                // reload or fetch this document id, which causes error alerts to pop up.
                this.deleted = true
                setTimeout(() => {
                    this.documentStore.deleteById(this.documentId)
                    this.documentTileStore.deleteById(this.documentId)
                }, 100)
            }
        })
    }

    cloneDocument = (): Promise<APIDataDocument> => {
        return this.api.Routes.Document.postClone(this.documentId)
    }

    downloadCSV = async (documentId: number): Promise<Blob> => {
        // @ts-expect-error we treat it as a blob
        return this.api.Routes.Document.getDownloadCsv(documentId)
    }

    trackPdfDownload = async (documentId: number): Promise<any> => {
        return this.api.Routes.Document.putHistoryPdfDownload(documentId)
    }

    get document() {
        return this.documentStore.findById(this.documentId)
    }

    get documentSchema() {
        // returns the schema at the revision version of the doc
        if (this.documentSchemaRevisionId) {
            const revision = this.documentSchemaRevisionStore?.findById(this.documentSchemaRevisionId)
            if (revision) {
                // merge the revision with the rest of the schema data
                return {
                    ...this.documentSchemaStore.findById(this.documentSchemaId),
                    schema: revision.schema
                }
            } else {
                return this.documentSchemaStore.findById(this.documentSchemaId)
            }
        } else {
            return this.documentSchemaStore.findById(this.documentSchemaId)
        }
    }
}

decorate(injectable(), DocumentViewController)
decorate(inject(DocumentStore), DocumentViewController, 0)
decorate(inject(DocumentSchemaStore), DocumentViewController, 1)
decorate(inject(DocumentSchemaRevisionStore), DocumentViewController, 2)
decorate(inject(DocumentTileStore), DocumentViewController, 3)
decorate(inject(Api), DocumentViewController, 4)

export { DocumentViewController }
